import React, { useState, useEffect } from 'react';
import { Search, Menu, X, ChevronUp, ChevronDown } from 'lucide-react';
import Button from '../../components/ui/Button';
import TableOfContents from '../privacy-policy/components/TableOfContents';
import PolicySection from '../privacy-policy/components/PolicySection';
import SearchHighlight from '../privacy-policy/components/SearchHighlight';
import PrintDownloadActions from '../privacy-policy/components/PrintDownloadActions';

const TermsOfService = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const termsData = {
    effectiveDate: 'September 9, 2025',
    lastUpdated: 'September 9, 2025',
    company: 'Epoxy Dogs LLC',
    service: 'SkillBinder',
    sections: [
      {
        id: 'eligibility',
        title: '1. Eligibility',
        content: `You must be at least 18 years old to use SkillBinder. By using the Service, you represent that you meet this requirement.`
      },
      {
        id: 'accounts',
        title: '2. Accounts',
        content: `• You are responsible for keeping your login credentials secure.
• You must provide accurate, complete information when creating an account.
• We reserve the right to suspend or terminate accounts that violate these Terms.`
      },
      {
        id: 'subscriptions-payments',
        title: '3. Subscriptions & Payments',
        content: `• Premium subscriptions are billed at $19.99/month (unless otherwise stated).
• Payments are processed securely via Stripe.
• Subscriptions automatically renew unless canceled before the billing date.
• Refunds are handled at our discretion and according to applicable law.`
      },
      {
        id: 'acceptable-use',
        title: '4. Acceptable Use',
        content: `You agree not to:
• Use the Service for unlawful or harmful purposes.
• Attempt to reverse-engineer, copy, or resell the platform without permission.
• Upload content that is illegal, offensive, or violates the rights of others.`
      },
      {
        id: 'career-advice-disclaimer',
        title: '5. Career Advice Disclaimer',
        content: `SkillBinder provides tools, resources, and guidance to support career development. We do not guarantee employment, promotions, or specific career outcomes. All actions taken using the Service are your responsibility.`
      },
      {
        id: 'intellectual-property',
        title: '6. Intellectual Property',
        content: `• All content, software, and branding are owned by Epoxy Dogs LLC.
• You may not use our trademarks or content without prior written permission.`
      },
      {
        id: 'termination',
        title: '7. Termination',
        content: `We may suspend or terminate your account if you violate these Terms. You may cancel your subscription anytime through your account settings.`
      },
      {
        id: 'limitation-of-liability',
        title: '8. Limitation of Liability',
        content: `SkillBinder is provided "as-is." To the fullest extent permitted by law, Epoxy Dogs LLC is not liable for indirect, incidental, or consequential damages from using the Service.`
      },
      {
        id: 'changes-to-terms',
        title: '9. Changes to Terms',
        content: `We may update these Terms periodically. Continued use of the Service means you accept the updated Terms.`
      },
      {
        id: 'governing-law',
        title: '10. Governing Law',
        content: `These Terms are governed by the laws of the State of Ohio, USA, without regard to conflict of law principles.`
      },
      {
        id: 'contact-us',
        title: '11. Contact Us',
        content: `For questions about these Terms:
📧 support@skillbinder.com
📬 Epoxy Dogs LLC, 5698 Lamplighter Dr., Girard, OH 44420`
      }
    ]
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = termsData?.sections;
      let current = '';

      sections?.forEach(section => {
        const element = document.getElementById(section?.id);
        if (element) {
          const rect = element?.getBoundingClientRect();
          if (rect?.top <= 100) {
            current = section?.id;
          }
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [termsData?.sections]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const filteredSections = termsData?.sections?.filter(section =>
    searchTerm === '' || 
    section?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    section?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Terms of Service</h1>
              <p className="text-sm text-gray-600 mt-1">
                Effective Date: {termsData?.effectiveDate} | Last Updated: {termsData?.lastUpdated}
              </p>
            </div>
            <div className="hidden lg:flex items-center space-x-4">
              <PrintDownloadActions policyData={termsData} />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t bg-white">
            <div className="px-4 py-1 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search terms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <PrintDownloadActions policyData={termsData} />
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Desktop Table of Contents */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-8">
              <TableOfContents
                sections={termsData?.sections}
                activeSection={activeSection}
                onSectionClick={scrollToSection}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-8">
                {/* Terms Header */}
                <div className="mb-2 pb-6 border-b">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">📜 Terms of Service</h2>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      Welcome to <strong>{termsData?.service}</strong>, operated by{' '}
                      <strong>{termsData?.company}</strong> ("we," "our," "us"). By using{' '}
                      <strong>{termsData?.service}</strong> ("the Service"), you agree to these Terms of Service 
                      ("Terms"). Please read carefully.
                    </p>
                  </div>
                </div>

                {/* Terms Sections */}
                <div className="space-y-8">
                  {filteredSections?.map((section, index) => (
                    <div key={section?.id} className="lg:hidden">
                      {/* Mobile Accordion Style */}
                      <button
                        onClick={() => toggleSection(section?.id)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 text-left">
                          {section?.title}
                        </h3>
                        {expandedSections?.[section?.id] ? 
                          <ChevronUp size={20} className="text-gray-500" /> : 
                          <ChevronDown size={20} className="text-gray-500" />
                        }
                      </button>
                      
                      {expandedSections?.[section?.id] && (
                        <div className="mt-4 p-4 bg-white border rounded-lg">
                          <SearchHighlight text={section?.content} searchTerm={searchTerm} />
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Desktop Full Display */}
                  <div className="hidden lg:block">
                    {filteredSections?.map((section, index) => (
                      <PolicySection
                        key={section?.id}
                        section={section}
                        searchTerm={searchTerm}
                        isActive={activeSection === section?.id}
                      />
                    ))}
                  </div>
                </div>

                {/* No Results */}
                {filteredSections?.length === 0 && (
                  <div className="text-center py-12">
                    <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                    <p className="text-gray-600">
                      Try adjusting your search terms or browse all sections above.
                    </p>
                    <Button
                      onClick={() => setSearchTerm('')}
                      variant="outline"
                      className="mt-4"
                    >
                      Clear Search
                    </Button>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-12 pt-8 border-t">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Last Updated:</strong> {termsData?.lastUpdated}
                      <br />
                      These terms may be updated periodically. Continued use of the Service means you accept the updated Terms.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;